import { useEffect, useRef } from 'react';

import { DataProviderQueryStatus } from '../CustomDash/components/DataProvider/types';
import { formatProviderErrorMessage } from '../CustomDash/components/DataProvider/utils/data-provider-results-utils';
import { useTabContentConfig } from '../CustomDash/components/PageConfigProvider/PageConfigProvider';
import { useIsTaxonomyResolved } from '../CustomDash/filters/contexts/FilterProvider';
import { useGaChartPreviewMeta } from './ga-chart-preview-meta-context';
import type {
  GlobalAiChartPreviewDataBranch,
  GlobalAiChartPreviewRuntimeSnapshot,
} from './global-ai-assistant-chart-preview-runtime';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';

/**
 * Updates global-assistant chart preview runtime from data-provider state (GA preview only).
 */
export function GlobalAiChartPreviewDataReporter({
  itemId,
  branch,
  status,
  error,
  data,
  chartType,
  metric,
}: {
  itemId: string;
  branch: GlobalAiChartPreviewDataBranch;
  status: DataProviderQueryStatus;
  error: Error | { message?: string } | null | undefined;
  data: unknown;
  chartType: string;
  metric: unknown;
}) {
  const ga = useGlobalAIAssistantOptional();
  const mergeRuntimeRef = useRef<
    | ((
        id: string,
        patch: Partial<GlobalAiChartPreviewRuntimeSnapshot>
      ) => void)
    | undefined
  >(undefined);
  mergeRuntimeRef.current = ga?.mergeChartPreviewRuntime;
  const addTimelineEventRef = useRef<
    ((messageId: string, event: string) => void) | undefined
  >(undefined);
  addTimelineEventRef.current = ga?.addChartPreviewTimelineEvent;
  const meta = useGaChartPreviewMeta();
  const taxonomyResolved = useIsTaxonomyResolved();
  const { primaryFilter } = useTabContentConfig();
  const lastSnapRef = useRef('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    lastSnapRef.current = '';
  }, [meta?.messageId]);

  useEffect(() => {
    const merge = mergeRuntimeRef.current;
    if (!merge || !meta || meta.itemId !== itemId) {
      return;
    }
    const { messageId } = meta;
    const errStr = formatProviderErrorMessage(error) ?? '';
    const phase = (() => {
      if (status === DataProviderQueryStatus.Fetching) {
        return 'fetching' as const;
      }
      if (status === DataProviderQueryStatus.Error) {
        return 'error' as const;
      }
      if (status === DataProviderQueryStatus.Success) {
        if (chartType === 'text') {
          return undefined;
        }
        return 'success' as const;
      }
      return 'filters_ready' as const;
    })();
    const errSuffix = errStr.length > 0 ? `; err:${errStr.slice(0, 160)}` : '';
    const diag = `data:${branch}; status:${status}; chart:${chartType}; metric:${String(metric)}; taxonomy:${String(taxonomyResolved)}; hasData:${Boolean(data)}; primary:${primaryFilter?.id ?? 'none'}${errSuffix}`;
    const snap = `${messageId}|${phase ?? 'text_provider_success'}|${diag}`;
    if (lastSnapRef.current === snap) {
      return;
    }
    lastSnapRef.current = snap;
    merge(messageId, {
      ...(phase !== undefined ? { phase } : {}),
      dataProviderBranch: branch,
      lastDataProviderStatus: String(status),
      providerDiagnostics: diag,
    });
    addTimelineEventRef.current?.(
      messageId,
      `provider ${branch} -> ${String(status)}`
    );
  }, [
    meta,
    itemId,
    branch,
    status,
    error,
    data,
    chartType,
    metric,
    taxonomyResolved,
    primaryFilter?.id,
  ]);

  return null;
}
